import type { FormSchema, FormSchemaResolved, ResolvableFormSchema } from '#layers/block-engine/types/form-schema';
import { set } from '#layers/base/public';
import { applyDefaultInputBlocks } from './apply-default-input-blocks';
import { visitFormSchemaDataFields } from './entries';

type ResolvableFormSchemaResult<
	TResult extends object,
	TContext,
> = ReturnType<ResolvableFormSchema<TResult, TContext>>;

/**
 * Define a form schema while preserving its generic value and context types.
 */
export function defineFormSchema<
	TResult extends object = Record<string, unknown>,
	TContext = unknown,
>(schema: FormSchema<TResult, TContext>) {
	return schema;
}

export interface ResolveFormSchemaOptions<
	TResult extends object,
	TContext = unknown,
> {
	/** Schema or schema factory to resolve. */
	schema: FormSchema<TResult, TContext>;
	/** Current root form values. */
	values: TResult;
	/**
	 * Context passed to schema getter function. Required when schema is a function.
	 */
	context?: TContext;
	/** Optional post-resolve schema transform. */
	transformSchema?: (schema: FormSchemaResolved<TResult>) => FormSchemaResolved<TResult>;
}

/**
 * Check whether a form schema needs runtime resolution.
 */
function isResolvableSchema<
	TResult extends object,
	TContext,
>(schema: FormSchema<TResult, TContext>): schema is ResolvableFormSchema<TResult, TContext> {
	return typeof schema === 'function';
}

/**
 * Resolve static and callback-backed form schemas into field arrays.
 */
export function resolveFormSchema<
	TResult extends object = Record<string, unknown>,
	TContext = unknown,
>(options: ResolveFormSchemaOptions<TResult, TContext>) {
	const resolved = isResolvableSchema(options.schema)
		? options.schema({
				form: (schema) => schema as ResolvableFormSchemaResult<TResult, TContext>,
				values: options.values,
				context: options.context as TContext,
			})
		: options.schema;

	const transformed = options.transformSchema ? options.transformSchema(resolved) : resolved;

	// Fill any field that lacks an explicit block/render with a default input
	// control inferred from its type, so every schema-driven form renders a
	// control. Runs last so a consumer transform can enrich fields (e.g. attach a
	// `type`) before inference, and so an explicit block always wins.
	return applyDefaultInputBlocks(transformed);
}

export interface GetDefaultValuesFromFormSchemaOptions<
	TResult extends object = Record<string, unknown>,
	TContext = unknown,
> {
	/** Schema to read default values from. */
	schema: FormSchema<TResult, TContext>;
	/**
	 * Optional context to pass to the schema if it's a function
	 */
	context?: TContext;
	/**
	 * Called when a field default throws. When omitted, default errors keep bubbling.
	 */
	onDefaultError?: (error: unknown, field: FormSchemaResolved<TResult>[number]) => void;
}

/**
 * Get an object with default values from a form schema.
 */
export function getDefaultValuesFromFormSchema<
	TResult extends object = Record<string, unknown>,
	TContext = unknown,
>(options: GetDefaultValuesFromFormSchemaOptions<TResult, TContext>) {
	const result: Record<string, unknown> = {};

	const resolvedSchema = resolveFormSchema({
		schema: options.schema,
		values: {} as TResult,
		context: (options.context ?? {}) as TContext,
	});

	visitFormSchemaDataFields(resolvedSchema, (field) => {
		if ('default' in field && field.default !== undefined) {
			try {
				set(result, field.name, field.default());
			}
			catch (error) {
				if (!options.onDefaultError) {
					throw error;
				}

				options.onDefaultError(error, field);
				set(result, field.name, undefined);
			}
		}
		else {
			set(result, field.name, undefined);
		}
	});

	return result;
}
